import { useState } from 'react';
import {
  UserRole,
  UserStatus,
  type UserDto,
  type UserStatus as UserStatusEnum,
} from '@wingseat/shared';
import { useAdminUsers, useUpdateAdminUser } from '../../hooks/use-admin';

const STATUS_COLORS: Record<UserDto['status'], string> = {
  INVITED: 'bg-slate-800 text-slate-300 border-slate-700',
  REGISTERED: 'bg-blue-900/40 text-blue-300 border-blue-800',
  EMAIL_PENDING: 'bg-amber-900/40 text-amber-300 border-amber-800',
  ACTIVE: 'bg-emerald-900/40 text-emerald-300 border-emerald-800',
  DISABLED: 'bg-rose-900/40 text-rose-300 border-rose-800',
};

export function AdminUsersPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<UserStatusEnum | ''>('');
  const [page, setPage] = useState(1);
  const list = useAdminUsers({
    search: search || undefined,
    status: status || undefined,
    page,
    pageSize: 20,
  });
  const update = useUpdateAdminUser();

  return (
    <div className="px-4 py-4 space-y-3">
      <h1 className="text-xl font-semibold">Nutzer</h1>

      <div className="flex gap-2">
        <input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Suche Name oder E-Mail"
          className="flex-1 rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm"
        />
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as UserStatusEnum | '');
            setPage(1);
          }}
          className="rounded-lg border border-slate-700 bg-slate-950 px-2 py-2 text-sm"
        >
          <option value="">Alle Status</option>
          {Object.values(UserStatus).map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {list.isLoading ? (
        <p className="py-12 text-center text-sm text-slate-400">Lädt …</p>
      ) : list.data && list.data.items.length > 0 ? (
        <ul className="space-y-2">
          {list.data.items.map((u) => (
            <li
              key={u.id}
              className="rounded-xl border border-slate-800 bg-slate-900/60 p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {u.firstName || '—'} {u.lastName || ''}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{u.email}</p>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] ${STATUS_COLORS[u.status]}`}
                >
                  {u.status}
                </span>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
                <span className="rounded border border-slate-700 bg-slate-800 px-2 py-1">
                  {u.role}
                </span>
                <select
                  value={u.role}
                  onChange={(e) =>
                    update.mutate({
                      id: u.id,
                      data: {
                        role: e.target.value as (typeof UserRole)[keyof typeof UserRole],
                      },
                    })
                  }
                  className="rounded border border-slate-700 bg-slate-800 px-2 py-1"
                >
                  {Object.values(UserRole).map((r) => (
                    <option key={r} value={r}>
                      Rolle: {r}
                    </option>
                  ))}
                </select>
                <select
                  value={u.status}
                  onChange={(e) =>
                    update.mutate({
                      id: u.id,
                      data: {
                        status: e.target.value as UserStatusEnum,
                      },
                    })
                  }
                  className="rounded border border-slate-700 bg-slate-800 px-2 py-1"
                >
                  {Object.values(UserStatus).map((s) => (
                    <option key={s} value={s}>
                      Status: {s}
                    </option>
                  ))}
                </select>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-12 text-center text-sm text-slate-400">
          Keine Treffer.
        </p>
      )}

      {list.data && list.data.total > list.data.pageSize && (
        <div className="flex items-center justify-between pt-2 text-sm">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-slate-700 px-3 py-1 disabled:opacity-40"
          >
            ‹
          </button>
          <span className="text-slate-400">
            Seite {page} / {Math.ceil(list.data.total / list.data.pageSize)}
          </span>
          <button
            disabled={page * list.data.pageSize >= list.data.total}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border border-slate-700 px-3 py-1 disabled:opacity-40"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
}
